(function () {
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const FORMSUBMIT_MAX_TOTAL = 5 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp'];

  function isAllowedImage(file) {
    if (ALLOWED_TYPES.includes(file.type)) return true;
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ALLOWED_EXT.includes(ext || '');
  }

  function compressImage(file, maxWidth, quality) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not compress image.'));
              return;
            }
            const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not read image.'));
      };

      img.src = objectUrl;
    });
  }

  async function prepareImage(file, maxBytes) {
    if (!isAllowedImage(file)) {
      throw new Error('INVALID_TYPE');
    }

    const widths = file.size > maxBytes ? [2000, 1600, 1400, 1200, 1000, 800, 640] : [1600, 1200];
    const qualities = file.size > maxBytes ? [0.85, 0.75, 0.65, 0.55, 0.45, 0.35] : [0.82, 0.72];

    let smallest = null;

    for (const width of widths) {
      for (const quality of qualities) {
        const compressed = await compressImage(file, width, quality);
        if (!smallest || compressed.size < smallest.size) {
          smallest = compressed;
        }
        if (compressed.size <= maxBytes) {
          return compressed;
        }
      }
    }

    if (smallest && smallest.size <= maxBytes) return smallest;
    throw new Error('COMPRESS_FAILED');
  }

  function appendHiddenField(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  function appendSingleFileField(form, file) {
    const input = document.createElement('input');
    input.type = 'file';
    input.name = 'attachment';

    if (typeof DataTransfer !== 'undefined') {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    }

    form.appendChild(input);
  }

  async function prepareFileForFormSubmit(file, root) {
    let prepared = await prepareImage(file, MAX_FILE_SIZE);
    if (prepared.size <= FORMSUBMIT_MAX_TOTAL) return prepared;

    prepared = await compressImage(file, 1200, 0.68);
    if (prepared.size <= FORMSUBMIT_MAX_TOTAL) return prepared;

    prepared = await compressImage(file, 900, 0.55);
    if (prepared.size <= FORMSUBMIT_MAX_TOTAL) return prepared;

    prepared = await compressImage(file, 720, 0.45);
    if (prepared.size <= FORMSUBMIT_MAX_TOTAL) return prepared;

    const error = new Error(root.dataset.errorSize || 'This photo is too large. Please choose a smaller image.');
    error.code = 'TOTAL_TOO_LARGE';
    throw error;
  }

  async function submitShopifyContact(root, payload) {
    const fd = new FormData();
    fd.append('form_type', 'contact');
    fd.append('utf8', '✓');
    fd.append('contact[email]', payload.email);
    if (payload.name) fd.append(`contact[${root.dataset.fieldName || 'Name'}]`, payload.name);
    if (payload.phone) fd.append(`contact[${root.dataset.fieldPhone || 'Phone number'}]`, payload.phone);
    if (payload.message) fd.append(`contact[${root.dataset.fieldComment || 'Comment'}]`, payload.message);

    const response = await fetch('/contact', {
      method: 'POST',
      body: fd,
      headers: { Accept: 'text/html' },
    });

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const hasSuccess = doc.querySelector('.form-status-list.form__message, .form__message .icon-success');
    const hasEmailError = doc.querySelector('a[href="#ContactForm-email"]');

    if (hasSuccess && !hasEmailError) return true;
    throw new Error(root.dataset.errorGeneric || 'Something went wrong. Please try again.');
  }

  function buildFormSubmitNextUrl() {
    const nextUrl = new URL(window.location.pathname, window.location.origin);
    const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewThemeId) nextUrl.searchParams.set('preview_theme_id', previewThemeId);
    nextUrl.searchParams.set('hs_contact_sent', '1');
    return nextUrl.toString();
  }

  async function submitFormSubmit(root, payload, files) {
    const action = root.dataset.formsubmitAction;
    if (!action) {
      throw new Error(root.dataset.errorGeneric || 'Something went wrong. Please try again.');
    }

    const preparedFile = await prepareFileForFormSubmit(files[0], root);
    const message = `${payload.message || ''}\n\n[1 photo attached]`;

    return new Promise((resolve, reject) => {
      const iframeName = `hs-formsubmit-${Date.now()}`;
      const iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.hidden = true;
      iframe.setAttribute('aria-hidden', 'true');

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      form.enctype = 'multipart/form-data';
      form.target = iframeName;
      form.style.display = 'none';

      appendHiddenField(form, 'name', payload.name);
      appendHiddenField(form, 'email', payload.email);
      appendHiddenField(form, '_replyto', payload.email);
      appendHiddenField(form, 'phone', payload.phone);
      appendHiddenField(form, 'message', message);
      appendHiddenField(form, '_subject', root.dataset.formSubject || 'New contact message');
      appendHiddenField(form, '_template', 'table');
      appendHiddenField(form, '_captcha', 'false');
      appendHiddenField(form, '_next', buildFormSubmitNextUrl());

      appendSingleFileField(form, preparedFile);

      let settled = false;
      let loadCount = 0;

      const finish = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        window.clearTimeout(fallbackId);
        form.remove();
        iframe.remove();
        if (error) reject(error);
        else resolve(true);
      };

      const tryFinishSuccess = () => {
        try {
          const href = iframe.contentWindow?.location?.href || '';
          if (href.includes('hs_contact_sent=1')) {
            finish(null);
            return true;
          }
        } catch (_error) {
          // FormSubmit thank-you page is cross-origin — submission was accepted.
          if (loadCount >= 1) {
            finish(null);
            return true;
          }
        }
        return false;
      };

      const timeoutId = window.setTimeout(() => {
        finish(new Error(root.dataset.errorGeneric || 'Something went wrong. Please try again.'));
      }, 20000);

      const fallbackId = window.setTimeout(() => {
        if (!settled && loadCount >= 1) finish(null);
      }, 3500);

      iframe.onload = () => {
        loadCount += 1;
        window.setTimeout(() => {
          tryFinishSuccess();
        }, 300);
      };

      document.body.appendChild(iframe);
      document.body.appendChild(form);
      form.submit();
    });
  }

  class HsContactForm {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('#ContactForm');
      this.bodyInput = root.querySelector('#ContactForm-body');
      this.fileInput = root.querySelector('#ContactForm-photos');
      this.grid = root.querySelector('#ContactForm-upload-grid');
      this.addButton = root.querySelector('#ContactForm-upload-add');
      this.hint = root.querySelector('#ContactForm-upload-hint');
      this.uploadError = root.querySelector('#ContactForm-upload-error');
      this.statusWrap = root.querySelector('#ContactForm-status');
      this.submitButton = root.querySelector('#ContactForm-submit');
      this.maxPhotos = 1;
      this.files = [];

      if (!this.form || !this.fileInput || !this.grid) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.fileInput.addEventListener('change', (event) => {
        this.onFilesSelected(event).catch(() => {
          this.showUploadError(this.root.dataset.errorGeneric || 'Something went wrong. Please try again.');
        });
      });
      this.grid.addEventListener('click', this.onGridClick.bind(this));
      this.renderPreviews();
    }

    onGridClick(event) {
      const removeBtn = event.target.closest('[data-remove-photo]');
      if (!removeBtn) return;
      event.preventDefault();
      const index = parseInt(removeBtn.dataset.removePhoto, 10);
      if (!Number.isFinite(index)) return;
      this.files.splice(index, 1);
      this.renderPreviews();
      this.syncFileInput();
    }

    setUploadBusy(isBusy) {
      if (this.addButton) {
        this.addButton.classList.toggle('is-busy', isBusy);
        this.addButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }
      if (this.fileInput) this.fileInput.disabled = isBusy;
    }

    async onFilesSelected(event) {
      const incoming = Array.from(event.target.files || []);
      event.target.value = '';
      if (!incoming.length) return;

      const file = incoming[0];
      this.clearUploadError();
      this.setUploadBusy(true);

      try {
        const prepared = await prepareImage(file, MAX_FILE_SIZE);
        this.files = [prepared];
        this.renderPreviews();
        this.syncFileInput();
        this.clearUploadError();
      } catch (error) {
        this.files = [];
        this.renderPreviews();
        this.syncFileInput();

        if (error.message === 'INVALID_TYPE') {
          this.showUploadError(this.root.dataset.errorType || 'Please choose JPG, PNG, or WEBP images only.');
        } else if (error.message === 'COMPRESS_FAILED') {
          this.showUploadError(this.root.dataset.errorSize || 'This photo is too large. Please choose a smaller image.');
        } else {
          this.showUploadError(this.root.dataset.errorRead || 'Could not read this photo. Please try another file.');
        }
      } finally {
        this.setUploadBusy(false);
      }
    }

    syncFileInput() {
      if (typeof DataTransfer === 'undefined') return;
      const dt = new DataTransfer();
      this.files.forEach((file) => dt.items.add(file));
      this.fileInput.files = dt.files;
    }

    renderPreviews() {
      this.grid.querySelectorAll('[data-photo-thumb]').forEach((node) => node.remove());

      this.files.forEach((file, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'hs-contact__upload-thumb';
        thumb.dataset.photoThumb = 'true';

        const img = document.createElement('img');
        img.alt = file.name;
        img.loading = 'lazy';
        img.src = URL.createObjectURL(file);
        img.onload = () => URL.revokeObjectURL(img.src);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'hs-contact__upload-remove';
        remove.dataset.removePhoto = String(index);
        remove.setAttribute('aria-label', (this.root.dataset.removeLabel || 'Remove photo') + ` ${index + 1}`);
        remove.innerHTML = '&times;';

        thumb.appendChild(img);
        thumb.appendChild(remove);
        this.grid.insertBefore(thumb, this.addButton);
      });

      if (this.addButton) {
        this.addButton.classList.toggle('hs-contact__upload-add--hidden', this.files.length >= this.maxPhotos);
        this.addButton.hidden = this.files.length >= this.maxPhotos;
      }

      if (this.hint) {
        this.hint.textContent =
          this.files.length >= this.maxPhotos
            ? (this.root.dataset.uploadFull || 'Photo added. Remove it to choose another.').trim()
            : (this.root.dataset.uploadHint || '').trim();
      }
    }

    clearError() {
      if (!this.statusWrap) return;
      this.statusWrap.hidden = true;
      this.statusWrap.innerHTML = '';
      this.statusWrap.classList.remove('hs-contact__status--error', 'hs-contact__status--success');
    }

    clearUploadError() {
      if (!this.uploadError) return;
      this.uploadError.hidden = true;
      this.uploadError.textContent = '';
    }

    showUploadError(message) {
      if (this.uploadError) {
        this.uploadError.hidden = false;
        this.uploadError.textContent = message;
      }
      this.uploadError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    showError(message) {
      if (!this.statusWrap) return;
      this.statusWrap.hidden = false;
      this.statusWrap.className = 'hs-contact__status hs-contact__status--error';
      this.statusWrap.innerHTML = `<p role="alert">${message}</p>`;
      this.statusWrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    showInfo(message) {
      if (!this.statusWrap) return;
      this.statusWrap.hidden = false;
      this.statusWrap.className = 'hs-contact__status hs-contact__status--info';
      this.statusWrap.innerHTML = `<p tabindex="-1">${message}</p>`;
      this.statusWrap.focus();
    }

    showSuccess(message) {
      if (!this.statusWrap) return;
      this.statusWrap.hidden = false;
      this.statusWrap.className = 'hs-contact__status hs-contact__status--success';
      this.statusWrap.innerHTML = `<p tabindex="-1">${message}</p>`;
      this.statusWrap.focus();
    }

    setSubmitting(isSubmitting) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isSubmitting;
      this.submitButton.classList.toggle('hs-contact__submit--busy', isSubmitting);
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    validate() {
      const emailInput = this.form.querySelector('#ContactForm-email');
      const email = emailInput ? emailInput.value.trim() : '';
      if (!email) {
        this.showError(this.root.dataset.errorEmail || 'Please enter your email address.');
        emailInput?.focus();
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError(this.root.dataset.errorEmailInvalid || 'Please enter a valid email address.');
        emailInput?.focus();
        return false;
      }
      return true;
    }

    async onSubmit(event) {
      event.preventDefault();
      this.clearError();
      if (!this.validate()) return;

      if (this.files.length > 0 && !this.root.dataset.formsubmitAction) {
        this.showError('Contact form is not configured.');
        return;
      }

      this.setSubmitting(true);

      try {
        const nameInput = this.form.querySelector('#ContactForm-name');
        const emailInput = this.form.querySelector('#ContactForm-email');
        const phoneInput = this.form.querySelector('#ContactForm-phone');
        const payload = {
          name: nameInput?.value.trim() || '',
          email: emailInput?.value.trim() || '',
          phone: phoneInput?.value.trim() || '',
          message: this.bodyInput?.value.trim() || '',
        };

        let successMessage = this.root.dataset.successMessage || 'Thanks for contacting us. We will get back to you soon.';

        if (this.files.length === 0) {
          await submitShopifyContact(this.root, payload);
        } else if (this.files.length === 1) {
          await submitFormSubmit(this.root, payload, this.files);
        } else {
          throw new Error(this.root.dataset.errorMax || 'Please attach only one photo.');
        }

        this.form.reset();
        this.files = [];
        this.renderPreviews();
        this.syncFileInput();
        this.clearUploadError();
        this.showSuccess(successMessage);
      } catch (error) {
        if (error.code === 'TOTAL_TOO_LARGE') {
          this.showUploadError(error.message);
        } else {
          this.showError(error.message || this.root.dataset.errorGeneric || 'Something went wrong. Please try again.');
        }
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-hs-contact-form]').forEach((root) => new HsContactForm(root));

    if (new URL(window.location.href).searchParams.get('hs_contact_sent') === '1') {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('hs_contact_sent');
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
